/**
 * The subscription's worker topics, in `OUTBOX_TOPIC`'s order (worker topics by the partition's complement):
 *
 * | Topic | Enqueued by, in the db_transaction of | Handler |
 * |---|---|---|
 * | `subscription.mail` | the applier of a purchase, change, renewal, suspension, expiry, cancellation or withdrawal; the notice pass | `subscription-mail` (MAIL): one of the ten `subscription_*` mails |
 * | `subscription.terms_change` | the operator's scheduling of a `vat_rate` change (design §32.7.9) | `subscription-notice` (WRK): one `subscription_terms_change_notice` per affected running subscription |
 */
export const SUBSCRIPTION_OUTBOX_TOPIC = ["subscription.mail", "subscription.terms_change"];
/**
 * The operator's subscription acts (design §32.9.1, plan-020 ED-18): each writes this `staff.operator_action` code
 * first (audit-or-abort, F3a) and, where the organization sees it, an `audit_entry` in the same db_transaction.
 *
 * | Act | `operator_action` | `audit_entry` |
 * |---|---|---|
 * | cancel (at period end, or now) | `subscription.cancel` | `subscription.cancellation_scheduled` / `subscription.cancelled_by_operator` |
 * | waive a period | `subscription_period.waive` | `subscription_period.waived` |
 * | credit a period | `subscription_period.credit` | `subscription_period.credited` |
 * | retry the card | `subscription_renewal.retry` | — |
 * | send a fresh pay link (the `billing_payment_request` mail, G9) | `subscription_renewal.pay_link_sent` | — |
 */
export const SUBSCRIPTION_OPERATOR_ACTION = Object.freeze({
    cancel: "subscription.cancel",
    periodWaive: "subscription_period.waive",
    periodCredit: "subscription_period.credit",
    renewalRetry: "subscription_renewal.retry",
    renewalPayLinkSent: "subscription_renewal.pay_link_sent",
});
/** The typed reason of an operator's subscription act (design §32.9.1); `other` still needs the free-text `reason`. */
export const SUBSCRIPTION_OPERATOR_REASON_CODE = [
    "customer_request",
    "payment_dispute",
    "service_issue",
    "goodwill",
    "card_problem",
    "other",
];
const SUBSCRIPTION_OPERATOR_REASON_CODE_SET = new Set(SUBSCRIPTION_OPERATOR_REASON_CODE);
/** Reads one operator reason code, throwing on anything else (the route maps the throw to `validation_failed`). */
export function subscriptionOperatorReasonCodeOf(value) {
    if (typeof value !== "string" || !SUBSCRIPTION_OPERATOR_REASON_CODE_SET.has(value)) {
        throw new RangeError(`${JSON.stringify(value)} is not a subscription operator reason code (one of ${SUBSCRIPTION_OPERATOR_REASON_CODE.join(", ")})`);
    }
    return value;
}
/** The free-text `reason` beside a reason code: 10–500 characters (design §32.9.1). */
export const SUBSCRIPTION_OPERATOR_REASON_LENGTH = Object.freeze({ min: 10, max: 500 });
/**
 * The codes plan-020 adds to `ERROR_CODE` (design §32.20), in its order — appended last there, once, by plan-020 DOM.
 * Reused, not re-spelled: `configuration_missing` (503, the public read), `offer_not_sellable`, `quote_stale`,
 * `quote_expired`, `mandate_unavailable`, `operation_state_conflict`.
 *
 * | Code | HTTP | Raised by |
 * |---|---|---|
 * | `subscription_sale_closed` | 409 | purchase, upgrade, reactivation quote and confirm while `subscriptionSale` is off |
 * | `subscription_tax_id_required` | 422 | the same, unless the shared balance's payer is a business with a valid tax id (`detail.reason`) |
 * | `subscription_consent_missing` | 422 | confirm with no consent, `agreed` false or another `text_revision` |
 * | `subscription_change_unavailable` | 422 | a change quote while `past_due` or `suspended` |
 * | `offer_scope_ineligible` | 422 | a purchase quote on a dedicated balance (OD-7) |
 * | `offer_term_unsupported` | 422 | a quote of an annual or trial revision (OA-6) |
 * | `payment_method_in_use_by_subscription` | 409 | removing the card a running subscription charges |
 * | `subscription_renewal_attempt_ceiling` | 409 | an operator retry past the period's merchant-initiated attempts |
 * | `subscription_renewal_unresolved` | 409 | an operator retry or card replacement while an attempt is being looked up |
 * | `subscription_tax_treatment_invalid` | fault | a plan fee resolved to a consumer treatment (logged with ids, never priced) |
 */
export const SUBSCRIPTION_ERROR_CODE = [
    "subscription_sale_closed",
    "subscription_tax_id_required",
    "subscription_consent_missing",
    "subscription_change_unavailable",
    "offer_scope_ineligible",
    "offer_term_unsupported",
    "payment_method_in_use_by_subscription",
    "subscription_renewal_attempt_ceiling",
    "subscription_renewal_unresolved",
    "subscription_tax_treatment_invalid",
];
/**
 * `subscription_tax_id_required`'s `detail.reason` (design §32.6.5, OA-4, ED-16): no tax id on the legal billing
 * profile, a tax id VIES called invalid, one not checked yet, or a check that answered pending or unavailable.
 */
export const SUBSCRIPTION_TAX_ID_REFUSAL_REASON = ["missing", "invalid", "unchecked", "try_later"];
//# sourceMappingURL=subscription.js.map